import { observer } from "mobx-react-lite"
import { AlertTriangle, X } from "lucide-react"
import { useState } from "react"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { cn } from "@/domains/library"

/**
 * Global indexer-freshness banner for the Manual CL surfaces. Observes
 * the shared {@link IndexerStatusStore} — which is sourced from Envio's
 * built-in `chain_metadata` table — and renders an amber / red status
 * bar when the indexer's committed progress block falls behind the
 * chain tip by more than 30 / 300 blocks (≈60 s / ≈10 min on Base).
 *
 * Freshness is derived from a block-delta (`progressBlock` vs
 * `headBlock`) rather than wall-clock versus an event timestamp, so a
 * quiet chain with no Manual CL events no longer reads as "behind".
 *
 * Diagnostic mode: if `localStorage.manualClDevMode === "1"` the banner
 * always renders with raw lag and block data, even when the store says
 * to hide it. Useful for debugging without recompiling.
 */
export const IndexerFreshnessBanner = observer(function IndexerFreshnessBanner({
  className,
}: { className?: string } = {}) {
  const DISMISS_KEY = "manualClIndexerBannerDismissed"
  const scaleApp = useScaleApplication()
  const store = scaleApp.ManualClV1.IndexerStatus
  const devMode = typeof window !== "undefined" && window.localStorage?.getItem("manualClDevMode") === "1"
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return window.sessionStorage?.getItem(DISMISS_KEY) === "1"
  })

  const onDismiss = () => {
    setDismissed(true)
    if (typeof window === "undefined") return
    window.sessionStorage?.setItem(DISMISS_KEY, "1")
  }

  if ((store.isHidden || dismissed) && !devMode) return null

  const level = store.level
  const lagSeconds = store.lagSeconds
  const lagBlocks = store.lagBlocks
  const lastBlock = store.status?.progressBlock

  const tone: "neutral" | "warning" | "stale" =
    !store.status ? "neutral" : level === "stale" ? "stale" : level === "warning" ? "warning" : "neutral"
  const secondaryTextClass = tone === "stale" ? "text-error-text/80" : "text-t200"

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="indexer-freshness-banner"
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-2 text-xs",
        tone === "stale" && "border-error-border/50 bg-error-background/10 text-error-text",
        // Keep warning/neutral tones readable on dark surfaces.
        tone === "warning" && "border-p300-light bg-p300 text-t100",
        tone === "neutral" && "border-p300-light bg-p300 text-t100",
        className,
      )}
    >
      <AlertTriangle
        className={cn("h-4 w-4 shrink-0", tone === "stale" ? "text-error-text" : "text-t200")}
        aria-hidden
      />
      <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-0.5">
        <span className="font-semibold">
          {!store.status
            ? "Indexer status unavailable"
            : level === "stale"
              ? "Indexer significantly behind head"
              : level === "warning"
                ? "Indexer slightly behind head"
                : "Indexer in sync"}
        </span>
        <span className={secondaryTextClass}>
          {store.status
            ? `${formatLag(lagSeconds)} behind (${lagBlocks.toLocaleString()} blocks)`
            : "Waiting for indexer status…"}
        </span>
        {lastBlock !== undefined && (
          <span className={secondaryTextClass}>Block #{lastBlock.toLocaleString()}</span>
        )}
      </div>
      {!devMode && (
        <button
          type="button"
          aria-label="Dismiss indexer status"
          onClick={onDismiss}
          className="rounded p-1 text-t200 transition-colors hover:bg-p200 hover:text-t100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
})

function formatLag(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}
